import { Card, CardBody, Flex, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { useGetBorrowingBaseReportsQuery } from '~/packages/borrowing-base/requests/borrowingBase.gql.generated';
import { BorrowingBaseSummaryWithSingleValue } from '~/packages/borrowing-base/ui/borrowingBaseSummary/BorrowingBaseSummaryWithSingleValue';
import { BorrowingBaseSummarySections } from '~/packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useGetCurrentCreditAgreementFullArtifactQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const displaySections: BorrowingBaseSummarySections[] = [
  BorrowingBaseSummarySections.receivables,
  BorrowingBaseSummarySections.cash,
  BorrowingBaseSummarySections.finalBorrowingBaseReport,
];

type PortfolioSummaryBorrowingBaseSummaryProps = {
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  creditFacilityId: string;
};
export const PortfolioSummaryBorrowingBaseSummary = ({
  reportingDate,
  fileVersion,
  creditFacilityId,
}: PortfolioSummaryBorrowingBaseSummaryProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const {
    data: borrowingBaseReportsData,
    loading: isBorrowingBaseReportsLoading,
  } = useGetBorrowingBaseReportsQuery({
    variables: {
      creditFacilityId,
      startReportingDate: reportingDate,
      endReportingDate: reportingDate,
    },
  });

  const {
    data: creditAgreementData,
    loading: creditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
    fetchPolicy: 'network-only',
  });

  if (
    !borrowingBaseReportsData ||
    !borrowingBaseReportsData.borrowingBaseReports ||
    isBorrowingBaseReportsLoading ||
    creditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="portfolio-summary-borrowing-base-summary-loading"
      />
    );
  }

  const {
    currentCreditAgreementFullArtifact: {
      creditAgreement,
      id: creditAgreementId,
    },
  } = creditAgreementData;

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
  } catch (err) {
    Sentry.captureException(err);

    toast({
      ...errorToastConfig,
      id: 'errorGetCurrentCreditAgreementFullArtifact',
      title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
    });
    navigate('/');
  }

  finleyAssert(
    parsedCreditAgreement,
    `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
  );

  const selectedBorrowingBaseReport = _.find(
    borrowingBaseReportsData.borrowingBaseReports,
    (report) => {
      return report.fileVersion === fileVersion;
    },
  );

  if (!selectedBorrowingBaseReport) {
    return null;
  }

  return (
    <Flex direction="column">
      <Typography variant="section-subtitle">Borrowing Base</Typography>
      <Card variant="outline" size="lg">
        <CardBody>
          <BorrowingBaseSummaryWithSingleValue
            sections={displaySections}
            borrowingBaseReport={selectedBorrowingBaseReport}
            creditAgreement={parsedCreditAgreement}
          />
        </CardBody>
      </Card>
    </Flex>
  );
};
